import React, { useState } from 'react';

export const DEFAULT_LOGOUT_HREF = '/account/logout?switcher=maybe';

// The seller dashboard surface is English-only today (see PLATFORM_LOCALE_SUPPORT in
// server/helpers/platformLocale.js — `es` is enabled for storefront/marketplace surfaces, not
// the dashboard). The selector is still wired to the `?lang=` convention so adding a locale
// here is the only change needed once one ships.
export const DEFAULT_LANGUAGE_OPTIONS = [{ value: 'en', label: 'English' }];

export function readTheme(doc = typeof document === 'undefined' ? null : document) {
  if (!doc) {
    return 'light';
  }
  const attribute = doc.documentElement.getAttribute('data-theme');
  if (attribute === 'dark' || attribute === 'light') {
    return attribute;
  }
  try {
    const stored = window.localStorage.getItem('mm-theme');
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
  } catch (error) {
    // localStorage can throw in private-mode Safari; fall through to the default.
  }
  return 'light';
}

/** Same contract as the inline toggle in dashboard/partials/appnav.ejs. */
export function applyTheme(theme) {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.setAttribute('data-theme', theme);
  try {
    window.localStorage.setItem('mm-theme', theme);
  } catch (error) {
    // Persisting the preference is best-effort.
  }
}

export function buildLanguageHref(locale, location) {
  const params = new URLSearchParams(location?.search || '');
  params.set('lang', locale);
  return `${location?.pathname || '/'}?${params.toString()}${location?.hash || ''}`;
}

/**
 * Mirrors the plain-DOM toggle in dashboard/partials/footer.ejs. The mobile drawer's open state
 * lives entirely on the shared `.appnav` DOM node (footer.ejs and this component both mount onto
 * it — see docs on `.appnav.r-seller-nav-sidebar` in SellerNav.css), not in React state, so
 * closing it here means writing the same classes rather than introducing a parallel state source.
 */
export function closeMobileNav(doc = typeof document === 'undefined' ? null : document) {
  if (!doc) {
    return;
  }
  const appnav = doc.querySelector('.appnav');
  const floatingTools = doc.querySelector('.floating-tools');
  const menuIcon = doc.getElementById('menu-icn');
  const sidebarLauncher = doc.querySelector('.sidebar-launcher');
  if (appnav) {
    appnav.classList.remove('open');
  }
  if (floatingTools) {
    floatingTools.classList.remove('nav-open');
  }
  if (menuIcon) {
    menuIcon.classList.remove('fa-times');
    menuIcon.classList.add('fa-bars');
  }
  // Keep the launcher's announced state in sync with the drawer footer.ejs opened; the inline
  // toggle writes aria-expanded, so the React close path must clear it the same way.
  if (sidebarLauncher) {
    sidebarLauncher.setAttribute('aria-expanded', 'false');
  }
}

export default function SellerNavFooter({
  logoutHref = DEFAULT_LOGOUT_HREF,
  languageOptions = DEFAULT_LANGUAGE_OPTIONS,
  currentLanguage = 'en',
}) {
  const [theme, setTheme] = useState(() => readTheme());
  const isDark = theme === 'dark';

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    applyTheme(next);
    setTheme(next);
  };

  const changeLanguage = (event) => {
    const locale = event.target.value;
    if (!locale || locale === currentLanguage || typeof window === 'undefined') {
      return;
    }
    window.location.assign(buildLanguageHref(locale, window.location));
  };

  const onlyOneLanguage = languageOptions.length < 2;

  // Three equal cells on one row, each stacking its icon over its label so the full text still
  // fits at a 320px sidebar width.
  return (
    <div className="seller-nav-sidebar__footer-wrap">
      <div className="seller-nav-sidebar__footer">
        <button
          type="button"
          className="seller-nav-sidebar__footer-control"
          onClick={toggleTheme}
          aria-pressed={isDark}
        >
          <span className="seller-nav-sidebar__footer-icon" aria-hidden="true">
            <i className={isDark ? 'fa-solid fa-moon' : 'fa-solid fa-sun'}></i>
          </span>
          <span className="seller-nav-sidebar__footer-label">
            {isDark ? 'Dark Mode' : 'Light Mode'}
          </span>
        </button>

        <div className="seller-nav-sidebar__footer-control seller-nav-sidebar__footer-control--language">
          <span className="seller-nav-sidebar__footer-icon" aria-hidden="true">
            <i className="fa-solid fa-globe"></i>
          </span>
          <select
            className="seller-nav-sidebar__footer-select"
            aria-label="Language"
            value={currentLanguage}
            onChange={changeLanguage}
            disabled={onlyOneLanguage}
            title={onlyOneLanguage ? 'English is the only language available right now' : undefined}
          >
            {languageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <a className="seller-nav-sidebar__footer-control" href={logoutHref}>
          <span className="seller-nav-sidebar__footer-icon" aria-hidden="true">
            <i className="fa-solid fa-right-from-bracket"></i>
          </span>
          <span className="seller-nav-sidebar__footer-label">Log Out</span>
        </a>
      </div>

      {/* Mobile-only: closes the same .appnav drawer the legacy .sidebar-launcher button opens.
          Hidden on desktop and hidden above the 800px breakpoint via CSS in SellerNav.css — see
          closeMobileNav() above for why this reaches into the DOM instead of React state. */}
      <button
        type="button"
        className="seller-nav-sidebar__footer-minimize"
        onClick={() => closeMobileNav()}
      >
        <i className="fa-solid fa-angles-left" aria-hidden="true"></i>
        <span>Minimize Menu</span>
      </button>
    </div>
  );
}
